import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import FeedbackCard from './components/FeedbackCard';
import FilterControls from './components/FilterControls';
import MetricsPanel from './components/MetricsPanel';
import ActivityTracker from './components/ActivityTracker';
import EmptyState from './components/EmptyState';

const FeedbackDashboard = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    dateRange: 'all'
  });

  // Mock feedback data
  const mockFeedback = [
    {
      id: 1,
      projectId: 'proj_001',
      projectName: 'Diamond Engagement Ring - Custom Setting',
      clientName: 'Sarah Johnson',
      clientEmail: 'sarah.johnson@email.com',
      status: 'approved',
      feedbackNote: `The design is absolutely stunning! I love the vintage-inspired setting and the way the side stones complement the center diamond. The proportions are perfect and exactly what I envisioned. Please proceed with production.`,
      submittedAt: new Date('2024-12-12T10:30:00'),
      viewedAt: new Date('2024-12-12T09:15:00')
    },
    {
      id: 2,
      projectId: 'proj_002',
      projectName: 'Anniversary Band - Eternity Style',
      clientName: 'Michael Chen',
      clientEmail: 'michael.chen@email.com',
      status: 'rejected',
      feedbackNote: `Thank you for the design. While I appreciate the craftsmanship, I'd like to make a few adjustments:\n\n1. Could we make the band slightly wider (maybe 3mm instead of 2.5mm)?\n2. I'd prefer round diamonds instead of princess cut\n3. The setting seems a bit high - could we lower it for everyday wear?\n\nLooking forward to seeing the revised version.`,
      submittedAt: new Date('2024-12-11T16:45:00'),
      viewedAt: new Date('2024-12-11T14:20:00')
    },
    {
      id: 3,
      projectId: 'proj_003',
      projectName: 'Pearl Necklace - Graduated Strand',
      clientName: 'Emily Rodriguez',
      clientEmail: 'emily.rodriguez@email.com',
      status: 'pending',
      feedbackNote: null,
      submittedAt: null,
      viewedAt: new Date('2024-12-10T11:30:00')
    },
    {
      id: 4,
      projectId: 'proj_004',
      projectName: 'Men\'s Wedding Band - Brushed Platinum',
      clientName: 'David Thompson',
      clientEmail: 'david.thompson@email.com',
      status: 'approved',
      feedbackNote: `Perfect! The brushed finish looks exactly as I hoped. The width and comfort fit are ideal. My fiancée and I are both thrilled with how this complements her engagement ring. Ready to move forward.`,
      submittedAt: new Date('2024-12-09T13:20:00'),
      viewedAt: new Date('2024-12-09T12:45:00')
    },
    {
      id: 5,
      projectId: 'proj_005',
      projectName: 'Sapphire Earrings - Art Deco Style',
      clientName: 'Jennifer Walsh',
      clientEmail: 'jennifer.walsh@email.com',
      status: 'rejected',
      feedbackNote: `The Art Deco inspiration is beautiful, but I have some concerns about the size. The earrings appear quite large in the renderings - could we scale them down by about 20%? Also, I'd prefer white gold instead of yellow gold to match my other jewelry. The sapphire color is gorgeous though!`,submittedAt: new Date('2024-12-08T09:15:00'),viewedAt: new Date('2024-12-08T08:30:00')
    },
    {
      id: 6,
      projectId: 'proj_006',projectName: 'Tennis Bracelet - 5 Carat Total Weight',clientName: 'Amanda Foster',clientEmail: 'amanda.foster@email.com',status: 'pending',feedbackNote: null,submittedAt: null,viewedAt: new Date('2024-12-07T15:45:00')
    }
  ];

  // Mock metrics data
  const mockMetrics = {
    approvalRate: 67,
    avgResponseTime: 18,
    pendingCount: 2,
    totalProjects: 6
  };

  // Mock activity data
  const mockActivities = [
    {
      clientName: 'Sarah Johnson',
      action: 'approved',
      projectName: 'Diamond Engagement Ring',
      type: 'approved',
      timestamp: new Date('2024-12-12T10:30:00')
    },
    {
      clientName: 'Michael Chen',
      action: 'provided feedback for',
      projectName: 'Anniversary Band',
      type: 'feedback',
      timestamp: new Date('2024-12-11T16:45:00')
    },
    {
      clientName: 'Emily Rodriguez',
      action: 'viewed',
      projectName: 'Pearl Necklace',
      type: 'viewed',
      timestamp: new Date('2024-12-10T11:30:00')
    },
    {
      clientName: 'David Thompson',
      action: 'approved',
      projectName: 'Men\'s Wedding Band',
      type: 'approved',
      timestamp: new Date('2024-12-09T13:20:00')
    },
    {
      clientName: 'Jennifer Walsh',
      action: 'requested changes for',
      projectName: 'Sapphire Earrings',
      type: 'rejected',
      timestamp: new Date('2024-12-08T09:15:00')
    }
  ];

  // Filter feedback based on current filters
  const filteredFeedback = mockFeedback?.filter(feedback => {
    const matchesSearch = filters?.search === '' || 
      feedback?.clientName?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
      feedback?.projectName?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
      feedback?.clientEmail?.toLowerCase()?.includes(filters?.search?.toLowerCase());

    const matchesStatus = filters?.status === 'all' || feedback?.status === filters?.status;

    const matchesDateRange = filters?.dateRange === 'all' || (() => {
      if (!feedback?.submittedAt) return filters?.dateRange === 'all';
      
      const now = new Date();
      const submittedDate = new Date(feedback.submittedAt);
      
      switch (filters?.dateRange) {
        case 'today':
          return submittedDate?.toDateString() === now?.toDateString();
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return submittedDate >= weekAgo;
        case 'month':
          return submittedDate?.getMonth() === now?.getMonth() && 
                 submittedDate?.getFullYear() === now?.getFullYear();
        case 'quarter':
          const quarter = Math.floor(now?.getMonth() / 3);
          const feedbackQuarter = Math.floor(submittedDate?.getMonth() / 3);
          return feedbackQuarter === quarter && 
                 submittedDate?.getFullYear() === now?.getFullYear();
        default:
          return true;
      }
    })();

    return matchesSearch && matchesStatus && matchesDateRange;
  });

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      dateRange: 'all'
    });
  };

  const handleViewDetails = (feedbackId) => {
    // Find the feedback item to get project details
    const feedback = mockFeedback?.find(f => f?.id === feedbackId);
    
    if (feedback) {
      // Navigate to client preview with project details
      navigate(`/client-preview/${feedback?.projectId}?client=${encodeURIComponent(feedback?.clientName)}&type=${encodeURIComponent(feedback?.projectName)}`);
    } else {
      console.log('Feedback not found:', feedbackId);
    }
  };

  const handleContactClient = (clientEmail) => {
    // Open email client or messaging system
    window.location.href = `mailto:${clientEmail}`;
  };

  const handleUpdateProject = (projectId) => {
    // Navigate to project update page
    navigate(`/create-project?update=${projectId}`);
  };

  const handleCreateProject = () => {
    navigate('/create-project');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-heading text-foreground mb-2">Feedback Dashboard</h1>
            <p className="text-muted-foreground">
              Monitor client responses and manage approval workflows for all your projects.
            </p>
          </div>

          {/* Metrics Panel */}
          <MetricsPanel metrics={mockMetrics} />

          {mockFeedback?.length === 0 ? (
            <EmptyState onCreateProject={handleCreateProject} />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6">
                {/* Filter Controls */}
                <FilterControls
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={handleClearFilters}
                  totalCount={mockFeedback?.length}
                  filteredCount={filteredFeedback?.length}
                />

                {/* Feedback Cards */}
                <div className="space-y-4">
                  {filteredFeedback?.length === 0 ? (
                    <div className="bg-card border border-border rounded-lg p-12 text-center">
                      <p className="text-muted-foreground">No feedback matches your current filters.</p>
                    </div>
                  ) : (
                    filteredFeedback?.map(feedback => (
                      <FeedbackCard
                        key={feedback?.id}
                        feedback={feedback}
                        onViewDetails={handleViewDetails}
                        onContactClient={handleContactClient}
                        onUpdateProject={handleUpdateProject}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <ActivityTracker activities={mockActivities} />
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default FeedbackDashboard;